import {
  STAGES,
  getStageMetaById,
  type StageId,
} from "@phaser/core/stages/StageCatalog";
import {
  getMonstersForStage,
  type StageConfig,
  type MonsterConfig,
} from "@/phaser/core/monsters/monsterCatalog";
import { OBSTACLES } from "@/phaser/core/obstacles/obstaclesCatalog";

interface PreparedStageData {
  stageId: StageId;
  stage: StageConfig;
  meta: ReturnType<typeof getStageMetaById>;
  monsters: MonsterConfig[];
}

/**
 * 게임 전역에서 사용하는 데이터 캐시
 *
 * 역할:
 * - 스테이지 메타, 몬스터 목록 등을 "한 번만 계산"
 * - 이후 GameScene / CodexScene 등에서 재사용
 *
 * 목적:
 * - 씬 진입 시 반복 계산 방지
 * - 성능 안정화 (특히 게임 재시작, 스테이지 변경 시)
 */
export class GameDataCache {
  // 스테이지별 준비된 데이터 저장
  private static preparedStages = new Map<StageId, PreparedStageData>();
  private static prepared = false;

  // 모든 스테이지 데이터를 미리 계산하여 캐시에 저장
  static prepareAll() {
    if (this.prepared) return;

    for (const s of STAGES) {
      const meta = getStageMetaById(s.id);
      const monsters = getMonstersForStage(meta.config);

      this.preparedStages.set(s.id, {
        stageId: s.id,
        stage: meta.config,
        meta,
        monsters,
      });
    }

    this.prepared = true;
  }

  // 특정 스테이지의 준비된 데이터 반환
  static getStageData(stageId: StageId): PreparedStageData {
    const data = this.preparedStages.get(stageId);
    if (!data) {
      throw new Error(`Stage data not prepared for stageId: ${stageId}`);
    }
    return data;
  }

  // 장애물 데이터 반환
  static getObstacles() {
    return OBSTACLES;
  }

  // 모든 스테이지 데이터 배열 반환
  static getAllStageData() {
    return Array.from(this.preparedStages.values());
  }
}
